/**
 * AI CONTEXT
 * Purpose: debug surface for the shared ArtworkWall (JARVIS-124) and the
 *   browser-run rendition backfill (JARVIS-122). The only place the wall is
 *   mounted until a real surface adopts it.
 * Key deps: studentAtoms + useAllDocs (this dashboard owns the data — the
 *   wall never reads Firestore), useRenditionBackfill, planRenditionBackfill.
 * Referenced by: App route /artwork-wall-debug (TEST_PAGE-gated).
 */
import { ExperimentOutlined } from "@ant-design/icons"
import {
  Alert,
  App,
  Button,
  Card,
  Descriptions,
  Input,
  InputNumber,
  Progress,
  Segmented,
  Statistic,
  Table,
  Typography,
} from "antd"
import { map } from "lodash"
import { useCallback, useMemo, useState } from "react"

import { studentAtoms } from "#atoms"
import Flex from "#blocks/Flex"
import Firestore from "#classes/Firestore"
import ArtworkWall from "#components/ArtworkWall/ArtworkWall"
import {
  buildWallItemsFromStudents,
  dedupeLatestVersions,
  describeZoomLevel,
} from "#components/ArtworkWall/artworkWallHelpers"
import {
  type ArtworkWallItem,
  type WallLayout,
} from "#components/ArtworkWall/artworkWallTypes"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { useAllDocs } from "#hooks/jotai/useSubscription"
import {
  BACKFILL_STATUS,
  useRenditionBackfill,
} from "#hooks/newHooks/useRenditionBackfill"
import {
  BACKFILL_OUTCOME,
  type BackfillPlan,
  type BackfillSkipped,
  limitBackfillCandidates,
  planRenditionBackfill,
} from "#utils/artworkRenditionBackfill"

const { Title, Text } = Typography

const STORAGE_KEY = "jarvis-artwork-wall-debug-student-id"

const SOURCE_MODE = {
  ALL: "all",
  ONE: "one",
} as const

type SourceMode = (typeof SOURCE_MODE)[keyof typeof SOURCE_MODE]

const readStoredStudentId = (): string =>
  localStorage.getItem(STORAGE_KEY)?.trim() ?? ""

const SKIP_REASON_LABEL: Record<BackfillSkipped["reason"], string> = {
  hidden: "Hidden (nondestructively deleted)",
  mobile: "Mobile record (has thumbnailUrl)",
  cropped: "Has mobile crop",
  foreignBucket: "Legacy original on another bucket",
  unparsableUrl: "URL is not a Storage download URL",
}

const ArtworkWallDebugDashboard = () => {
  const { message } = App.useApp()
  const students = useAllDocs<StudentConstructor>(
    studentAtoms,
    DOC_TYPES.STUDENTS
  )

  const [sourceMode, setSourceMode] = useState<SourceMode>(SOURCE_MODE.ALL)
  const [inputId, setInputId] = useState(readStoredStudentId)
  const [activeStudentId, setActiveStudentId] = useState(readStoredStudentId)
  const [dedupeVersions, setDedupeVersions] = useState(true)
  const [lastEvent, setLastEvent] = useState("—")
  // Tile clicks also fire onZoomChange (entering the strip), which lands last
  // in the shared readout, so the activation gets its own readout.
  const [lastTile, setLastTile] = useState("—")

  const selectedStudents = useMemo(
    () =>
      sourceMode === SOURCE_MODE.ALL
        ? students
        : students.filter((s) => s.id === activeStudentId),
    [activeStudentId, sourceMode, students]
  )

  const items = useMemo(
    () => buildWallItemsFromStudents(selectedStudents),
    [selectedStudents]
  )
  const dedupedCount = useMemo(
    () => dedupeLatestVersions(items).items.length,
    [items]
  )

  const applyStudentId = useCallback(() => {
    const next = inputId.trim()
    if (!next) {
      void message.warning("Enter a student id")
      return
    }
    localStorage.setItem(STORAGE_KEY, next)
    setActiveStudentId(next)
    setSourceMode(SOURCE_MODE.ONE)
    void message.success(`Watching student ${next}`)
  }, [inputId, message])

  // --- Backfill -----------------------------------------------------------

  const appBucket = Firestore.getInstance().getAppBucket()
  const [plan, setPlan] = useState<BackfillPlan | null>(null)
  // Debug-only: caps how many PLANNED candidates a run processes (plan order),
  // so the e2e specs can prove the mechanism on a few items. Never limits the
  // scan itself. Empty = run everything, which is what an operator wants.
  const [runLimit, setRunLimit] = useState<number | null>(null)
  const backfill = useRenditionBackfill()
  const isRunning = backfill.status === BACKFILL_STATUS.RUNNING
  const runCandidates = useMemo(
    () => (plan ? limitBackfillCandidates(plan.candidates, runLimit) : []),
    [plan, runLimit]
  )

  const scan = useCallback(() => {
    backfill.reset()
    const next = planRenditionBackfill(students, appBucket)
    setPlan(next)
    void message.info(
      `${next.candidates.length} candidates, ${next.skipped.length} skipped`
    )
  }, [appBucket, backfill, message, students])

  const run = useCallback(() => {
    if (runCandidates.length === 0) return
    void backfill.start(runCandidates)
  }, [backfill, runCandidates])

  const failures = useMemo(
    () => backfill.results.filter((r) => r.outcome === BACKFILL_OUTCOME.FAILED),
    [backfill.results]
  )

  const onTileClick = useCallback((item: ArtworkWallItem, index: number) => {
    const description = `tile #${index} (${item.id}) by ${item.studentName ?? "?"}`
    setLastEvent(description)
    setLastTile(description)
  }, [])
  const onZoomChange = useCallback(
    (zoomIndex: number) =>
      setLastEvent(`zoom → ${describeZoomLevel(zoomIndex)}`),
    []
  )
  const onLayoutChange = useCallback(
    (layout: WallLayout) => setLastEvent(`layout → ${layout}`),
    []
  )

  const percent =
    backfill.tally.total === 0
      ? 0
      : Math.round((backfill.tally.processed / backfill.tally.total) * 100)

  return (
    <Flex
      col
      gap={16}
      style={{ padding: 40, maxWidth: 1400, margin: "0 auto" }}
    >
      <Title level={3}>
        <ExperimentOutlined /> Artwork Wall Debug Dashboard
      </Title>

      <Text type="secondary">
        Mounts the shared <Text code>ArtworkWall</Text> against{" "}
        <Text code>students.*.artwork</Text> (this page owns the subscription;
        the wall never reads Firestore) and hosts the browser-run rendition
        backfill. Bucket: <Text code>{appBucket ?? "unknown"}</Text>.
      </Text>

      <Card size="small" title="Source">
        <Flex gap={12} wrap style={{ alignItems: "center" }}>
          <Segmented
            data-testid="wall-source-mode"
            value={sourceMode}
            onChange={(value) => setSourceMode(value as SourceMode)}
            options={[
              {
                label: `All students (${students.length})`,
                value: SOURCE_MODE.ALL,
              },
              { label: "One student", value: SOURCE_MODE.ONE },
            ]}
          />
          <Input
            style={{ maxWidth: 360 }}
            value={inputId}
            onChange={(e) => setInputId(e.target.value)}
            placeholder="Student document id"
            onPressEnter={applyStudentId}
          />
          <Button onClick={applyStudentId}>Apply & save</Button>
          <Segmented
            data-testid="wall-dedupe-toggle"
            value={dedupeVersions ? "on" : "off"}
            onChange={(value) => setDedupeVersions(value === "on")}
            options={[
              { label: "Latest versions", value: "on" },
              { label: "All versions", value: "off" },
            ]}
          />
        </Flex>
        <Flex gap={32} style={{ marginTop: 12 }}>
          <Statistic
            title="Students in scope"
            value={selectedStudents.length}
            data-testid="stat-students"
          />
          <Statistic
            title="Artwork entries"
            value={items.length}
            data-testid="stat-artwork"
          />
          <Statistic
            title="Tiles after dedupe"
            value={dedupedCount}
            data-testid="stat-tiles"
          />
          <Descriptions size="small" column={1} style={{ maxWidth: 420 }}>
            <Descriptions.Item label="Last wall event">
              <Text code data-testid="wall-last-event">
                {lastEvent}
              </Text>
            </Descriptions.Item>
            <Descriptions.Item label="Last tile click">
              <Text code data-testid="wall-last-tile">
                {lastTile}
              </Text>
            </Descriptions.Item>
          </Descriptions>
        </Flex>
      </Card>

      <Card
        size="small"
        title="Wall"
        styles={{ body: { padding: 0, height: "70vh" } }}
      >
        <ArtworkWall
          items={items}
          dedupeVersions={dedupeVersions}
          onTileClick={onTileClick}
          onZoomChange={onZoomChange}
          onLayoutChange={onLayoutChange}
          emptyText={
            students.length === 0
              ? "Waiting for students…"
              : "No artwork for this selection"
          }
        />
      </Card>

      <Card size="small" title="Rendition backfill (browser-run, idempotent)">
        <Flex col gap={12}>
          <Text type="secondary">
            Scans every loaded student, writes <Text code>_thumb.jpg</Text> /{" "}
            <Text code>_med.jpg</Text> next to each app-bucket original that
            lacks them, and skips any that already has both. Records it cannot
            write (mobile, cropped, other bucket) are listed, not dropped. The
            limit only caps a run (first N in plan order) — the scan always
            covers everything.
          </Text>
          <Flex gap={8} wrap>
            <Button
              onClick={scan}
              disabled={isRunning || students.length === 0}
              data-testid="backfill-scan"
            >
              Scan
            </Button>
            <InputNumber
              min={1}
              precision={0}
              placeholder="All"
              value={runLimit}
              onChange={(value) =>
                setRunLimit(typeof value === "number" ? value : null)
              }
              disabled={isRunning}
              style={{ width: 120 }}
              addonBefore="Limit"
              aria-label="Run limit (debug only)"
              data-testid="backfill-limit"
            />
            <Button
              type="primary"
              onClick={run}
              disabled={runCandidates.length === 0 || isRunning}
              loading={isRunning}
              data-testid="backfill-run"
            >
              {plan && runCandidates.length < plan.candidates.length
                ? `Run backfill (first ${runCandidates.length} of ${plan.candidates.length})`
                : "Run backfill"}
            </Button>
            <Button
              danger
              onClick={backfill.abort}
              disabled={!isRunning}
              data-testid="backfill-abort"
            >
              Abort
            </Button>
            <Text data-testid="backfill-status">
              Status: <Text code>{backfill.status}</Text>
            </Text>
          </Flex>

          {plan ? (
            <Flex gap={32} wrap>
              <Statistic
                title="Candidates"
                value={plan.candidates.length}
                data-testid="backfill-candidates"
              />
              {map(plan.skippedByReason, (count, reason) => (
                <Statistic
                  key={reason}
                  title={SKIP_REASON_LABEL[reason as BackfillSkipped["reason"]]}
                  value={count}
                  data-testid={`backfill-skipped-${reason}`}
                />
              ))}
            </Flex>
          ) : null}

          {backfill.status !== BACKFILL_STATUS.IDLE ? (
            <>
              <Progress
                percent={percent}
                status={isRunning ? "active" : undefined}
              />
              <Flex gap={32} wrap data-testid="backfill-tally">
                <Statistic
                  title="Processed"
                  value={`${backfill.tally.processed} / ${backfill.tally.total}`}
                  data-testid="backfill-processed"
                />
                <Statistic
                  title="Written"
                  value={backfill.tally.written}
                  data-testid="backfill-written"
                />
                <Statistic
                  title="Already existed"
                  value={backfill.tally.existing}
                  data-testid="backfill-existing"
                />
                <Statistic
                  title="Failed"
                  value={backfill.tally.failed}
                  data-testid="backfill-failed"
                />
              </Flex>
            </>
          ) : null}

          {backfill.results.length > 0 ? (
            <ul
              data-testid="backfill-results"
              style={{
                margin: 0,
                paddingLeft: 20,
                maxHeight: 160,
                overflowY: "auto",
                fontSize: 12,
              }}
            >
              {map(backfill.results, (result) => (
                <li
                  key={result.candidate.artworkId}
                  data-testid="backfill-result"
                  data-artwork-id={result.candidate.artworkId}
                  data-outcome={result.outcome}
                >
                  <Text code>{result.outcome}</Text>{" "}
                  {result.candidate.studentName} —{" "}
                  <Text code>{result.candidate.artworkId}</Text>
                </li>
              ))}
            </ul>
          ) : null}

          {failures.length > 0 ? (
            <Alert
              type="error"
              showIcon
              message={`${failures.length} rendition write(s) failed`}
              description={
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {map(failures, (f) => (
                    <li key={f.candidate.artworkId}>
                      <Text code>{f.candidate.objectPath}</Text> — {f.error}
                    </li>
                  ))}
                </ul>
              }
            />
          ) : null}

          {plan && plan.skipped.length > 0 ? (
            <Table<BackfillSkipped>
              size="small"
              rowKey="artworkId"
              pagination={{ pageSize: 10 }}
              dataSource={plan.skipped}
              data-testid="backfill-skipped-table"
              columns={[
                { title: "Student", dataIndex: "studentName" },
                { title: "Artwork", dataIndex: "artworkId" },
                {
                  title: "Reason",
                  dataIndex: "reason",
                  render: (reason: BackfillSkipped["reason"]) =>
                    SKIP_REASON_LABEL[reason],
                },
                {
                  title: "URL",
                  dataIndex: "url",
                  ellipsis: true,
                  render: (url: string) => <Text code>{url}</Text>,
                },
              ]}
            />
          ) : null}
        </Flex>
      </Card>
    </Flex>
  )
}

export default ArtworkWallDebugDashboard
